/**
 * ADMIN INCENTIVES API
 *
 * These hooks call /admin/incentives endpoints and require admin permissions.
 * Only use for users with awards_admin or global_admin permissions.
 *
 * For regular user award/incentive claiming, use @bonusly/user-awards instead.
 *
 * Use these admin hooks directly in admin interfaces that need full
 * incentive management capabilities.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { getHttpClient, HttpClient, useHttpClient } from '@bonusly/http-client';
import { getQueryClient } from '@bonusly/query-client';
import { Permission, usePermission } from '@bonusly/who-am-i';

import { GroupType } from '../schemas/groups';
import {
	ClaimLimitPeriod,
	Incentive,
	IncentiveDetail,
	IncentiveMetadata,
	IncentiveState,
	IncentiveVisibility,
	RecentClaim,
	RecentClaimTimeframe,
} from '../schemas/incentives';
import { listOf } from '../schemas/utils';
import { ensureID, Metadata, PaginatedData, PaginationParams, RecordOrID } from './meta';
import { UserAPIData } from './users';

/*
 * Shared utilities
 */

enum QueryKey {
	Incentives = 'incentives:incentives',
	RecentClaims = 'incentives:recentClaims',
	Claimability = 'incentives:claimability',
}

const headers = {
	'Key-Inflection': 'camel',
};

/*
 * Path and query key builders
 */

export const incentiveQueryKey = (id: string) => [QueryKey.Incentives, id];
export const incentiveClaimabilityQueryKey = (id: string) => [QueryKey.Claimability, id];
export const incentivesQueryKey = (params?: Record<string, any>) => [QueryKey.Incentives, params].filter(Boolean);
export const recentClaimsQueryKey = () => [QueryKey.RecentClaims];

export const incentivesPath = (prefix = '') => `${prefix}/admin/incentives`;
export const incentivePath = (id: string, prefix = '') => `${prefix}/admin/incentives/${id}`;
export const incentiveClaimabilityPath = (id: string, prefix = '') => `${prefix}/incentives/metadata/${id}/claimability`;
export const recentClaimsPath = (prefix = '') => `${prefix}/incentives/recent_claims`;

type IncentiveAPIData = {
	id: string;
	amount: number;
	budget_period_type: string;
	description: string;
	giver_id: string;
	icon_url: string | null;
	name: string;
	reason: string;
	require_proof: boolean;
	state: string;
	visibility: string;
};

/*
 * Index queries
 */

type IncentivesIndexParams = PaginationParams<{
	state?: 'active' | 'inactive';
}>;

export const useIncentives = (params?: IncentivesIndexParams) => {
	const http = useHttpClient();
	const canAccessIncentives = usePermission(Permission.CanAccessIncentives);
	const allParams = { state: 'active', ...params };

	return useQuery({
		queryKey: incentivesQueryKey(allParams),
		queryFn: async () => {
			const res = await http.get<PaginatedData<IncentiveAPIData>>(incentivesPath(), {
				params: allParams,
				headers,
			});

			const incentives = listOf(Incentive).parse(res.data.records);
			return [incentives, new Metadata(res.data.metadata)] as const;
		},
		enabled: canAccessIncentives,
	});
};

type RecentClaimAPIData = {
	timespan: RecentClaimTimeframe;
	incentive: IncentiveAPIData;
	receivers: UserAPIData;
};

export const useRecentClaims = () => {
	const http = useHttpClient();
	const canAccessIncentives = usePermission(Permission.CanAccessIncentives);

	return useQuery({
		queryKey: recentClaimsQueryKey(),
		queryFn: async () => {
			const res = await http.get<PaginatedData<RecentClaimAPIData>>(recentClaimsPath(), {
				headers,
			});

			return listOf(RecentClaim).parse(res.data.records);
		},
		enabled: canAccessIncentives,
	});
};

type IncentiveClaimabilityAPIData = {
	incentive: IncentiveAPIData;
	user: UserAPIData;
	claimable: boolean;
};

export const useIncentiveMetadata = (id?: string) => {
	const http = useHttpClient();
	const canAccessIncentives = usePermission(Permission.CanAccessIncentives);

	return useQuery({
		// FIXME: We shouldn't even be using this hook if there isn't an ID to query on. It suggests a poor application of state in the
		// rendering component. This is probably worth refactoring as part of BOUNTY-1
		queryKey: incentiveClaimabilityQueryKey(id ?? ''),
		queryFn: async () => {
			const res = await http.get<IncentiveClaimabilityAPIData>(incentiveClaimabilityPath(id ?? ''), {
				headers,
			});

			return IncentiveMetadata.parse(res.data);
		},
		enabled: !!id && canAccessIncentives,
	});
};

/*
 * Detail queries
 */

const createIncentiveFetcher = (http: HttpClient, id: string) => async () => {
	const res = await http.get<IncentiveAPIData>(incentivePath(id), { headers });
	return IncentiveDetail.parse(res.data);
};

export const useAdminIncentive = (id: string) => {
	const canAccessIncentives = usePermission(Permission.CanAccessIncentives);

	return useQuery({
		queryKey: incentiveQueryKey(id),
		queryFn: createIncentiveFetcher(useHttpClient(), id),
		enabled: !!id && canAccessIncentives,
	});
};

export const loadAdminIncentive = async (id: string) => {
	const http = await getHttpClient();
	const queryClient = await getQueryClient();

	return queryClient.fetchQuery({
		queryKey: incentiveQueryKey(id),
		queryFn: createIncentiveFetcher(http, id),
	});
};

/*
 * Update mutations
 */

type ApprovalProcessLevel = {
	position: number;
	allowedApproverIds: string[];
};

type ClaimerEmailContentAPIData = {
	subjectLine: string | null;
	header: string | null;
	body: string | null;
};

type ClaimerEmailCustomizationAPIData = {
	sendClaimApprovalEmail: boolean;
	approvalEmail: ClaimerEmailContentAPIData | null;
	rejectionEmail: ClaimerEmailContentAPIData | null;
};

export type MutateIncentiveAPIData = {
	amount: number;
	claimLimit: number | null;
	periodType: ClaimLimitPeriod;
	description: string;
	icon?: File | null;
	name: string;
	quantity: number | null;
	message: string;
	requireProof: boolean;
	approvalProcessLevels: ApprovalProcessLevel[] | null;
	conditionsOperator: GroupType | null;
	conditions: { attr: string; value: string }[] | null;
	visibility: IncentiveVisibility;
	state?: IncentiveState;
	claimerEmailCustomization?: ClaimerEmailCustomizationAPIData | null;
};

type MutateIncentiveOptions = {
	onError?: (error: unknown) => void;
	onMutate?: () => void;
	onSuccess?: (incentive?: Incentive) => void;
};

export const useDeactivateIncentive = (recordOrId: RecordOrID, options: MutateIncentiveOptions = {}) => {
	const http = useHttpClient();
	const store = useStoreIncentive();
	const invalidate = useInvalidateIncentives();

	return useMutation({
		mutationFn: async () => {
			try {
				options.onMutate?.();

				const res = await http.patch(incentivePath(ensureID(recordOrId)), { state: 'inactive' }, { headers });
				const record = IncentiveDetail.parse(res.data);
				store(record);
				invalidate();
				options.onSuccess?.(record);
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};

export const useUpdateIncentive = (recordOrId: RecordOrID, options: MutateIncentiveOptions = {}) => {
	const http = useHttpClient();
	const store = useStoreIncentive();
	const invalidate = useInvalidateIncentives();

	return useMutation({
		mutationFn: async (data: MutateIncentiveAPIData | FormData) => {
			try {
				options.onMutate?.();

				const res = await http.patch(incentivePath(ensureID(recordOrId)), data, { headers });
				const record = IncentiveDetail.parse(res.data);
				store(record);
				invalidate();
				options.onSuccess?.(record);
				return record;
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};

/*
 * Delete mutations
 */

export const useDeleteIncentive = (recordOrId: RecordOrID, options: MutateIncentiveOptions = {}) => {
	const http = useHttpClient();
	const invalidate = useInvalidateIncentives();

	return useMutation({
		mutationFn: async () => {
			try {
				await http.delete(incentivePath(ensureID(recordOrId)), { headers });
				invalidate();
				options.onSuccess?.();
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};

/*
 * Create mutations
 */

export const useCreateIncentive = (options: MutateIncentiveOptions = {}) => {
	const http = useHttpClient();
	const store = useStoreIncentive();
	const invalidate = useInvalidateIncentives();

	return useMutation({
		mutationFn: async (data: MutateIncentiveAPIData | FormData) => {
			options.onMutate?.();

			try {
				const res = await http.post(incentivesPath(), data, { headers });
				const record = IncentiveDetail.parse(res.data);
				store(record);
				invalidate();
				options.onSuccess?.(record);
				return record;
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};

/*
 * Cache management
 */

export const useStoreIncentive = () => {
	const queryClient = useQueryClient();

	return useCallback(
		(record: IncentiveDetail) => {
			queryClient.setQueryData<Incentive>(incentiveQueryKey(record.id), record);
		},
		[queryClient]
	);
};

export const useInvalidateIncentives = () => {
	const client = useQueryClient();

	return useCallback(() => {
		client.invalidateQueries(incentivesQueryKey());
	}, [client]);
};
